import { useState, useCallback, useEffect } from "react";
import { analytics } from "../lib/analytics";

export interface XPData {
  xp: number;
  level: number;
  nextLevelXP: number;
  currentLevelProgress: number; // 0 to 1
}

export interface XPActions {
  addXP: (amount: number, reason?: string) => void;
  getXPForNextLevel: () => number;
  reset: () => void;
}

export interface UseXPReturn extends XPData, XPActions {}

const XP_STORAGE_KEY = "flowcus_user_xp";
const LEVEL_STORAGE_KEY = "flowcus_user_level";

// XP required for each level (exponential growth)
const calculateXPForLevel = (level: number): number => {
  return Math.floor(1000 * Math.pow(1.15, level - 1));
};

// Calculate total XP needed to reach a level
const calculateTotalXPForLevel = (level: number): number => {
  let total = 0;
  for (let i = 1; i < level; i++) {
    total += calculateXPForLevel(i);
  }
  return total;
};

// Calculate level from total XP
const calculateLevelFromXP = (xp: number): number => {
  let level = 1;
  let totalXP = 0;

  while (totalXP + calculateXPForLevel(level) <= xp) {
    totalXP += calculateXPForLevel(level);
    level++;
  }

  return level;
};

export const useXP = (): UseXPReturn => {
  // Initialize state from localStorage
  const [xp, setXP] = useState(() => {
    const stored = localStorage.getItem(XP_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : 0;
  });

  const [level, setLevel] = useState(() => {
    const stored = localStorage.getItem(LEVEL_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : 1;
  });

  // Calculate derived values
  const nextLevelXP = calculateXPForLevel(level);
  const currentLevelXP = xp - calculateTotalXPForLevel(level);
  const currentLevelProgress = nextLevelXP > 0 ? currentLevelXP / nextLevelXP : 0;

  // Persist to localStorage whenever xp or level changes
  useEffect(() => {
    localStorage.setItem(XP_STORAGE_KEY, xp.toString());
  }, [xp]);

  useEffect(() => {
    localStorage.setItem(LEVEL_STORAGE_KEY, level.toString());
  }, [level]);

  const addXP = useCallback(
    (amount: number, reason?: string) => {
      if (amount <= 0) return;

      setXP(prevXP => {
        const newXP = prevXP + amount;
        const newLevel = calculateLevelFromXP(newXP);

        // Check for level up
        if (newLevel > level) {
          setLevel(newLevel);

          // Track achievement unlock for level up
          analytics.track("achievement_unlocked", {
            achievement_id: `level_${newLevel}`,
            achievement_name: `Level ${newLevel}`,
            category: "progression",
            rarity:
              newLevel <= 5
                ? "common"
                : newLevel <= 10
                  ? "uncommon"
                  : newLevel <= 20
                    ? "rare"
                    : "epic",
          });

          // Trigger level up event/notification
          console.log(`Level ${newLevel} reached! 🎉`);

          // Create custom event for level up
          const levelUpEvent = new CustomEvent("levelUp", {
            detail: {
              newLevel,
              previousLevel: level,
              totalXP: newXP,
              xpGained: amount,
              reason,
            },
          });
          window.dispatchEvent(levelUpEvent);

          // Show achievement notification if possible
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification(`Level ${newLevel} Achieved! 🎉`, {
              body: `You've earned ${amount} XP${reason ? ` for ${reason}` : ""}!`,
              icon: "/logo192.png",
            });
          }
        } else {
          // Track regular XP gain with PostHog
          const nextLevelProgress =
            ((newXP - calculateTotalXPForLevel(newLevel)) / calculateXPForLevel(newLevel)) * 100;
          analytics.track("xp_earned", {
            amount: amount,
            source:
              (reason as
                | "task_completion"
                | "streak"
                | "focus_session"
                | "achievement"
                | "unknown") || "unknown",
            current_level: newLevel,
            next_level_progress: Math.round(nextLevelProgress),
          });

          // Regular XP gain event
          const xpGainEvent = new CustomEvent("xpGain", {
            detail: {
              amount,
              reason,
              totalXP: newXP,
              currentLevel: level,
            },
          });
          window.dispatchEvent(xpGainEvent);
        }

        return newXP;
      });
    },
    [level]
  );

  const getXPForNextLevel = useCallback(() => {
    return nextLevelXP - currentLevelXP;
  }, [nextLevelXP, currentLevelXP]);

  const reset = useCallback(() => {
    setXP(0);
    setLevel(1);
    localStorage.removeItem(XP_STORAGE_KEY);
    localStorage.removeItem(LEVEL_STORAGE_KEY);
  }, []);

  return {
    xp,
    level,
    nextLevelXP,
    currentLevelProgress,
    addXP,
    getXPForNextLevel,
    reset,
  };
};
